import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Upload, Settings, X, Truck, ShoppingBag, Users, Search, Image as ImageIcon, Download, FileUp } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { config } from '../../config/environment';
import { useBodyLock } from '../../hooks/useBodyLock';

interface Produto {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  imagem_url: string;
  categoria_id: number;
  categoria_nome: string;
  ativo: boolean;
  destaque: boolean;
  tempo_preparo: number;
  codigo?: string;
  codigo_barras?: string;
  opcoes_personalizacao?: OpcaoPersonalizacao[];
  visivel_delivery: boolean;
  visivel_garcom: boolean;
  visivel_pdv: boolean;
  venda_por_peso: boolean;
  fiscal_ncm?: string;
  fiscal_cst?: string;
  fiscal_cfop?: string;
  fiscal_cest?: string;
  fiscal_ibpt_fed?: number;
  fiscal_ibpt_est?: number;
  fiscal_ibpt_mun?: number;
  fiscal_pis_cst?: string;
  fiscal_cofins_cst?: string;
  fiscal_pis_p?: number;
  fiscal_cofins_p?: number;
  fiscal_origem?: number;
  fiscal_p_red_bc?: number;
  fiscal_csosn?: string;
  created_at?: string;
  updated_at?: string;
}

interface Categoria {
  id: number;
  nome: string;
}

interface OpcaoPersonalizacao {
  id?: number;
  nome: string;
  tipo: 'adicionar' | 'remover';
  obrigatorio: boolean;
  max_selecoes: number;
  itens: ItemOpcao[];
}

interface ItemOpcao {
  id?: number;
  nome: string;
  preco_adicional: number;
  quantidade: number;
}

interface ProdutoForm {
  nome: string;
  descricao: string;
  preco: number;
  categoria_id: number;
  tempo_preparo: number;
  destaque: boolean;
  codigo?: string;
  codigo_barras?: string;
  visivel_delivery: boolean;
  visivel_garcom: boolean;
  visivel_pdv: boolean;
  venda_por_peso: boolean;
  fiscal_ncm?: string;
  fiscal_cst?: string;
  fiscal_cfop?: string;
  fiscal_cest?: string;
  fiscal_ibpt_fed?: number;
  fiscal_ibpt_est?: number;
  fiscal_ibpt_mun?: number;
  fiscal_pis_cst?: string;
  fiscal_cofins_cst?: string;
  fiscal_pis_p?: number;
  fiscal_cofins_p?: number;
  fiscal_origem?: number;
  fiscal_p_red_bc?: number;
  fiscal_csosn?: string;
}

interface Tributo {
  codigo: string;
  descricao: string;
  nacionalfederal: number;
  importadosfederal: number;
  estadual: number;
  municipal: number;
}

const ProdutosPage: React.FC = () => {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPersonalizacaoModal, setShowPersonalizacaoModal] = useState(false);
  const [editingProduto, setEditingProduto] = useState<Produto | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [opcoesPersonalizacao, setOpcoesPersonalizacao] = useState<OpcaoPersonalizacao[]>([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState<number | ''>('');
  const [filtroVisibilidade, setFiltroVisibilidade] = useState<{
    delivery: boolean | null;
    pdv: boolean | null;
    garcom: boolean | null;
  }>({
    delivery: null,
    pdv: null,
    garcom: null
  });
  const [codigoAutoGerado, setCodigoAutoGerado] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showCopiarModal, setShowCopiarModal] = useState(false);
  const [produtosCopia, setProdutosCopia] = useState<Produto[]>([]);
  const [buscaCopia, setBuscaCopia] = useState('');
  const [loadingCopia, setLoadingCopia] = useState(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 20;

  const formatarData = (dataStr?: string) => {
    if (!dataStr) return '';
    try {
      const d = new Date(dataStr);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      // Debounce para busca no servidor
      fetchProdutos(true, searchTerm, categoriaFiltro);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, categoriaFiltro]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (showCopiarModal) {
        setLoadingCopia(true);
        try {
          const response = await api.get(`/api/produtos?limit=50&search=${encodeURIComponent(buscaCopia)}`);
          setProdutosCopia(response.data.data || response.data);
        } catch (error) {
          toast.error('Erro ao buscar produtos');
        } finally {
          setLoadingCopia(false);
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [buscaCopia, showCopiarModal]);

  const exportToCSV = async () => {
    try {
      const loadingToast = toast.loading('Carregando todos os produtos para exportação...');
      const response = await api.get('/api/produtos'); // Sem limit
      const todosProdutos = response.data.data || response.data;
      toast.dismiss(loadingToast);

      if (todosProdutos.length === 0) {
        toast.error('Não há produtos para exportar');
        return;
      }

      // Arrays de campos para iterar mais fácil
      const columns = [
        { header: 'ID', key: 'id', type: 'number' },
        { header: 'Nome', key: 'nome', type: 'string' },
        { header: 'Descricao', key: 'descricao', type: 'string' },
        { header: 'Preco', key: 'preco', type: 'decimal' },
        { header: 'Categoria_ID', key: 'categoria_id', type: 'number' },
        { header: 'Categoria_Nome', key: 'categoria_nome', type: 'readonly' },
        { header: 'Ativo', key: 'ativo', type: 'boolean' },
        { header: 'Destaque', key: 'destaque', type: 'boolean' },
        { header: 'Tempo_Preparo', key: 'tempo_preparo', type: 'number' },
        { header: 'Codigo', key: 'codigo', type: 'stringText' },
        { header: 'Codigo_Barras', key: 'codigo_barras', type: 'stringText' },
        { header: 'Visivel_Delivery', key: 'visivel_delivery', type: 'boolean' },
        { header: 'Visivel_Garcom', key: 'visivel_garcom', type: 'boolean' },
        { header: 'Visivel_PDV', key: 'visivel_pdv', type: 'boolean' },
        { header: 'Venda_Por_Peso', key: 'venda_por_peso', type: 'boolean' },
        { header: 'Fiscal_NCM', key: 'fiscal_ncm', type: 'stringText' },
        { header: 'Fiscal_Origem', key: 'fiscal_origem', type: 'number' },
        { header: 'Fiscal_CST', key: 'fiscal_cst', type: 'stringText' },
        { header: 'Fiscal_CSOSN', key: 'fiscal_csosn', type: 'stringText' },
        { header: 'Fiscal_CFOP', key: 'fiscal_cfop', type: 'stringText' },
        { header: 'Fiscal_CEST', key: 'fiscal_cest', type: 'stringText' },
        { header: 'Fiscal_P_Red_BC', key: 'fiscal_p_red_bc', type: 'decimal' },
        { header: 'Fiscal_PIS_CST', key: 'fiscal_pis_cst', type: 'stringText' },
        { header: 'Fiscal_COFINS_CST', key: 'fiscal_cofins_cst', type: 'stringText' },
        { header: 'Fiscal_PIS_P', key: 'fiscal_pis_p', type: 'decimal' },
        { header: 'Fiscal_COFINS_P', key: 'fiscal_cofins_p', type: 'decimal' },
        { header: 'Fiscal_IBPT_Fed', key: 'fiscal_ibpt_fed', type: 'decimal' },
        { header: 'Fiscal_IBPT_Est', key: 'fiscal_ibpt_est', type: 'decimal' },
        { header: 'Fiscal_IBPT_Mun', key: 'fiscal_ibpt_mun', type: 'decimal' }
      ];

      const escapeCSV = (val: any) => {
        if (val === null || val === undefined) return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
      };
      
      const escapeNumText = (val: any) => {
        if (val === null || val === undefined || val === '') return '""';
        // Usando a sintaxe de formula ="" obriga o Excel a carregar como texto puro e ignorar formatações, salvando os zeros à esquerda do NCM
        return `="""${String(val).replace(/"/g, '""')}"""`;
      };

      const formatDecimal = (val: any) => {
        return escapeCSV(parseFloat(val || 0).toString().replace('.', ','));
      };

      const formatBoolean = (val: any) => escapeCSV(val ? '1' : '0');

      const headers = columns.map(c => c.header).join(';');
      
      const rows = todosProdutos.map((p: any) => {
        return columns.map(c => {
          const val = (p as any)[c.key];
          switch(c.type) {
            case 'number': return escapeCSV(val || '0');
            case 'boolean': return formatBoolean(val);
            case 'decimal': return formatDecimal(val);
            case 'stringText': return escapeNumText(val);
            case 'string': 
            case 'readonly': return escapeCSV(val || '');
            default: return escapeCSV(val || '');
          }
        }).join(';');
      });

      const csvContent = [headers, ...rows].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `produtos_finansys_completo_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Todos os de produtos exportados!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar produtos');
    }
  };

  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        
        // Simples CSV parser considering quotes
        const parseLine = (line: string) => {
          let result = [];
          let current = '';
          let inQuotes = false;
          for(let i=0; i<line.length; i++) {
            if(line[i] === '"') {
              if(inQuotes && line[i+1] === '"') { current += '"'; i++; }
              else { inQuotes = !inQuotes; }
            }
            else if(line[i] === ';' && !inQuotes) {
              result.push(current);
              current = '';
            }
            else { current += line[i]; }
          }
          result.push(current);
          return result;
        };

        const lines = text.split('\n').filter(line => line.trim() !== '');
        if (lines.length < 2) {
          toast.error('Arquivo vazio ou inválido');
          return;
        }

        const headers = parseLine(lines[0].replace('\ufeff', '')).map(h => h.trim());
        const dataRows = lines.slice(1);

        const parsedProdutos = dataRows.map(line => {
          const values = parseLine(line);
          const p: any = {};
          
          headers.forEach((h, index) => {
            let val = values[index];
            if (val === undefined || val === null) return;
            val = val.trim();
            // Remoção da formula de texto do Excel
            if (val.startsWith('="') && val.endsWith('"')) { val = val.substring(2, val.length - 1); }
            if (val.startsWith('="""') && val.endsWith('"""')) { val = val.substring(4, val.length - 3); }
            if (val === '') return;

            switch(h) {
              case 'ID': p.id = parseInt(val); break;
              case 'Nome': p.nome = val; break;
              case 'Descricao': p.descricao = val; break;
              case 'Preco': p.preco = parseFloat(val.replace(',', '.')); break;
              case 'Categoria_ID': p.categoria_id = parseInt(val); break;
              case 'Ativo': p.ativo = val === '1' || val.toLowerCase() === 'true'; break;
              case 'Destaque': p.destaque = val === '1' || val.toLowerCase() === 'true'; break;
              case 'Tempo_Preparo': p.tempo_preparo = parseInt(val); break;
              case 'Codigo': p.codigo = val; break;
              case 'Codigo_Barras': p.codigo_barras = val; break;
              case 'Visivel_Delivery': p.visivel_delivery = val === '1' || val.toLowerCase() === 'true'; break;
              case 'Visivel_Garcom': p.visivel_garcom = val === '1' || val.toLowerCase() === 'true'; break;
              case 'Visivel_PDV': p.visivel_pdv = val === '1' || val.toLowerCase() === 'true'; break;
              case 'Venda_Por_Peso': p.venda_por_peso = val === '1' || val.toLowerCase() === 'true'; break;
              case 'Fiscal_NCM': p.fiscal_ncm = val; break;
              case 'Fiscal_Origem': p.fiscal_origem = parseInt(val); break;
              case 'Fiscal_CST': p.fiscal_cst = val; break;
              case 'Fiscal_CSOSN': p.fiscal_csosn = val; break;
              case 'Fiscal_CFOP': p.fiscal_cfop = val; break;
              case 'Fiscal_CEST': p.fiscal_cest = val; break;
              case 'Fiscal_P_Red_BC': p.fiscal_p_red_bc = parseFloat(val.replace(',', '.')); break;
              case 'Fiscal_PIS_CST': p.fiscal_pis_cst = val; break;
              case 'Fiscal_COFINS_CST': p.fiscal_cofins_cst = val; break;
              case 'Fiscal_PIS_P': p.fiscal_pis_p = parseFloat(val.replace(',', '.')); break;
              case 'Fiscal_COFINS_P': p.fiscal_cofins_p = parseFloat(val.replace(',', '.')); break;
              case 'Fiscal_IBPT_Fed': p.fiscal_ibpt_fed = parseFloat(val.replace(',', '.')); break;
              case 'Fiscal_IBPT_Est': p.fiscal_ibpt_est = parseFloat(val.replace(',', '.')); break;
              case 'Fiscal_IBPT_Mun': p.fiscal_ibpt_mun = parseFloat(val.replace(',', '.')); break;
            }
          });
          return p;
        }).filter(p => p.id);

        if (parsedProdutos.length === 0) {
          toast.error('Nenhum produto válido encontrado para atualizar');
          return;
        }

        const confirmImport = window.confirm(`Deseja importar e atualizar todos os dados de ${parsedProdutos.length} produtos? Essa ação reescreverá completamente os itens listados.`);
        if (!confirmImport) return;

        const loadingToast = toast.loading('Importando produtos...');
        try {
          await api.post('/api/produtos/bulk', { produtos: parsedProdutos });
          toast.success(`${parsedProdutos.length} produtos atualizados com sucesso!`, { id: loadingToast });
          fetchProdutos(true);
        } catch (err: any) {
          toast.error(err.response?.data?.error || 'Erro na importação em massa', { id: loadingToast });
        }
      } catch (error) {
        console.error('Erro ao ler CSV:', error);
        toast.error('Erro ao processar o arquivo CSV');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };
  
  // Estados para busca de tributos
  const [showTributosModal, setShowTributosModal] = useState(false);
  const [tributosResults, setTributosResults] = useState<Tributo[]>([]);
  const [tributosQuery, setTributosQuery] = useState('');
  const [searchingTributos, setSearchingTributos] = useState(false);

  useBodyLock(showModal || showPersonalizacaoModal);

  const fetchCategorias = async () => {
    try {
      const response = await api.get('/api/categorias');
      setCategorias(response.data);
    } catch (error) {
      toast.error('Erro categorias');
    }
  };

  const fetchProdutos = async (reset = false, searchStr = searchTerm, categoriaId = categoriaFiltro) => {
    try {
      if (reset) {
        if (initialLoading) setLoading(true);
        else setIsSearching(true);
      }
      const currentPage = reset ? 0 : page;
      
      let url = `/api/produtos?limit=${LIMIT}&offset=${currentPage * LIMIT}`;
      if (searchStr) url += `&search=${encodeURIComponent(searchStr)}`;
      if (categoriaId) url += `&categoria_id=${categoriaId}`;

      const response = await api.get(url);
      const novosProdutos = response.data.data || response.data;
      
      if (reset) {
        setProdutos(novosProdutos);
      } else {
        setProdutos(prev => [...prev, ...novosProdutos]);
      }
      
      setHasMore(novosProdutos.length === LIMIT);
      setPage(currentPage + 1);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
      setInitialLoading(false);
      setIsSearching(false);
    }
  };

  const fetchOpcoesPersonalizacao = async (produtoId: number) => {
    try {
      const response = await api.get(`/api/produtos/${produtoId}/opcoes`);
      setOpcoesPersonalizacao(response.data);
    } catch (error) {
      setOpcoesPersonalizacao([]);
    }
  };

  const gerarCodigoAutomatico = async (): Promise<string> => {
    try {
      const response = await api.get('/api/produtos/proximo-codigo');
      return response.data.codigo;
    } catch (error) {
      return Math.floor(1000 + Math.random() * 9000).toString();
    }
  };

  const { register, handleSubmit, reset, setValue, getValues, watch, formState: { errors } } = useForm<ProdutoForm>({
    defaultValues: { 
      visivel_delivery: true, 
      visivel_garcom: true, 
      visivel_pdv: true, 
      venda_por_peso: false, 
      codigo_barras: '',
      fiscal_ibpt_fed: 0, 
      fiscal_ibpt_est: 0, 
      fiscal_ibpt_mun: 0,
      fiscal_origem: 0,
      fiscal_pis_cst: '01',
      fiscal_cofins_cst: '01',
      fiscal_pis_p: 0,
      fiscal_cofins_p: 0,
      fiscal_p_red_bc: 0,
      fiscal_cst: '00',
      fiscal_csosn: '102'
    }
  });

  const searchTributos = async (queryStr: string) => {
    if (!queryStr || queryStr.length < 2) {
      setTributosResults([]);
      return;
    }
    setSearchingTributos(true);
    try {
      const response = await api.get(`/api/tributos/search?q=${queryStr}`);
      setTributosResults(response.data);
    } catch (error) {
      toast.error('Erro na busca');
    } finally {
      setSearchingTributos(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tributosQuery) searchTributos(tributosQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [tributosQuery]);

  const selectTributo = (t: Tributo) => {
    setValue('fiscal_ncm', t.codigo, { shouldValidate: true });
    setValue('fiscal_ibpt_fed', t.nacionalfederal);
    setValue('fiscal_ibpt_est', t.estadual);
    setValue('fiscal_ibpt_mun', t.municipal);
    setShowTributosModal(false);
    toast.success('Tributos anexados!');
  };

  useEffect(() => {
    fetchCategorias();
    // fetchProdutos() já é chamado pelo useEffect do searchTerm/categoriaFiltro na montagem
  }, []);

  const handleFinalSubmit = async (data: ProdutoForm) => {
    try {
      const formData = new FormData();
      formData.append('nome', data.nome.trim());
      formData.append('descricao', data.descricao || '');
      formData.append('preco', data.preco.toString());
      formData.append('categoria_id', data.categoria_id.toString());
      formData.append('tempo_preparo', (data.tempo_preparo || 0).toString());
      formData.append('destaque', data.destaque ? '1' : '0');
      formData.append('visivel_delivery', data.visivel_delivery ? '1' : '0');
      formData.append('visivel_pdv', data.visivel_pdv ? '1' : '0');
      formData.append('visivel_garcom', data.visivel_garcom ? '1' : '0');
      formData.append('venda_por_peso', data.venda_por_peso ? '1' : '0');
      formData.append('codigo', data.codigo || codigoAutoGerado);
      if (data.codigo_barras) formData.append('codigo_barras', data.codigo_barras);
      
      // Novos campos fiscais
      if (data.fiscal_ncm) formData.append('fiscal_ncm', data.fiscal_ncm);
      if (data.fiscal_cst) formData.append('fiscal_cst', data.fiscal_cst);
      if (data.fiscal_cfop) formData.append('fiscal_cfop', data.fiscal_cfop);
      if (data.fiscal_cest) formData.append('fiscal_cest', data.fiscal_cest);
      if (data.fiscal_ibpt_fed !== undefined && data.fiscal_ibpt_fed !== null) formData.append('fiscal_ibpt_fed', data.fiscal_ibpt_fed.toString());
      if (data.fiscal_ibpt_est !== undefined && data.fiscal_ibpt_est !== null) formData.append('fiscal_ibpt_est', data.fiscal_ibpt_est.toString());
      if (data.fiscal_ibpt_mun !== undefined && data.fiscal_ibpt_mun !== null) formData.append('fiscal_ibpt_mun', data.fiscal_ibpt_mun.toString());
      
      // Adicionando novos campos fiscais para o FormData
      if (data.fiscal_origem !== undefined && data.fiscal_origem !== null) formData.append('fiscal_origem', data.fiscal_origem.toString());
      if (data.fiscal_csosn) formData.append('fiscal_csosn', data.fiscal_csosn);
      if (data.fiscal_pis_cst) formData.append('fiscal_pis_cst', data.fiscal_pis_cst);
      if (data.fiscal_cofins_cst) formData.append('fiscal_cofins_cst', data.fiscal_cofins_cst);
      if (data.fiscal_pis_p !== undefined && data.fiscal_pis_p !== null) formData.append('fiscal_pis_p', data.fiscal_pis_p.toString());
      if (data.fiscal_cofins_p !== undefined && data.fiscal_cofins_p !== null) formData.append('fiscal_cofins_p', data.fiscal_cofins_p.toString());
      if (data.fiscal_p_red_bc !== undefined && data.fiscal_p_red_bc !== null) formData.append('fiscal_p_red_bc', data.fiscal_p_red_bc.toString());

      if (editingProduto) formData.append('ativo', editingProduto.ativo ? '1' : '0');
      if (selectedImage) formData.append('imagem', selectedImage);

      let produtoId: number;
      if (editingProduto) {
        await api.put(`/api/produtos/${editingProduto.id}`, formData);
        produtoId = editingProduto.id;
        toast.success('Atualizado!');
      } else {
        const response = await api.post('/api/produtos', formData);
        produtoId = response.data.id;
        toast.success('Criado!');
      }

      if (opcoesPersonalizacao.length > 0) {
        await api.post(`/api/produtos/${produtoId}/opcoes`, { opcoes: opcoesPersonalizacao });
      }

      setShowModal(false);
      reset();
      fetchProdutos(true);
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao salvar';
      toast.error(msg);
    }
  };

  const onSubmit = (data: ProdutoForm) => handleFinalSubmit(data);

  const handleSaveAndOpenPersonalizacao = async () => {
    const data = getValues();
    
    // Validação robusta antes de abrir personalização
    if (!data.nome?.trim() || !data.preco || !data.categoria_id || !data.fiscal_ncm || !data.fiscal_cst || !data.fiscal_cfop) {
      toast.error('Preencha todos os campos obrigatórios (*), incluindo os Dados Fiscais.');
      return;
    }
    
    await handleFinalSubmit(data);
    setShowPersonalizacaoModal(true);
  };

  const salvarPersonalizacao = async () => {
    if (!editingProduto) return;
    try {
      await api.post(`/api/produtos/${editingProduto.id}/opcoes`, { opcoes: opcoesPersonalizacao });
      toast.success('Salvo!');
      setShowPersonalizacaoModal(false);
      fetchProdutos(true);
    } catch (error) {
      toast.error('Erro');
    }
  };

  const handleEdit = async (produto: Produto) => {
    setEditingProduto(produto);
    reset({ ...produto, codigo: produto.codigo || '', codigo_barras: produto.codigo_barras || '' });
    setSelectedImage(null);
    await fetchOpcoesPersonalizacao(produto.id);
    setShowModal(true);
  };

  const handlePersonalizacao = async (produto: Produto) => {
    setEditingProduto(produto);
    await fetchOpcoesPersonalizacao(produto.id);
    setShowPersonalizacaoModal(true);
  };

  const handleDesativar = async (id: number, ativo: boolean) => {
    if (window.confirm('Mudar status?')) {
      try {
        await api.patch(`/api/produtos/${id}/ativo`, { ativo: !ativo });
        toast.success(`Produto ${ativo ? 'inativado' : 'ativado'} com sucesso!`);
        fetchProdutos(true);
      } catch (error) {
        toast.error('Erro ao mudar status do produto');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Excluir?')) {
      try {
        await api.delete(`/api/produtos/${id}`);
        toast.success('Produto excluído com sucesso!');
        fetchProdutos(true);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Erro ao excluir produto');
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setSelectedImage(e.target.files[0]);
  };

  const addOpcao = (tipo: 'adicionar' | 'remover') => {
    setOpcoesPersonalizacao([...opcoesPersonalizacao, { nome: tipo === 'adicionar' ? 'Adicionais' : 'Remoções', tipo, obrigatorio: false, max_selecoes: 1, itens: [] }]);
  };
  const removeOpcao = (idx: number) => setOpcoesPersonalizacao(opcoesPersonalizacao.filter((_, i) => i !== idx));
  const updateOpcao = (idx: number, f: keyof OpcaoPersonalizacao, v: any) => {
    const n = [...opcoesPersonalizacao];
    n[idx] = { ...n[idx], [f]: v };
    setOpcoesPersonalizacao(n);
  };
  const addItemOpcao = (oIdx: number) => {
    const n = [...opcoesPersonalizacao];
    n[oIdx].itens.push({ nome: '', preco_adicional: 0, quantidade: 1 });
    setOpcoesPersonalizacao(n);
  };
  const removeItemOpcao = (oIdx: number, iIdx: number) => {
    const n = [...opcoesPersonalizacao];
    n[oIdx].itens.splice(iIdx, 1);
    setOpcoesPersonalizacao(n);
  };
  const updateItemOpcao = (oIdx: number, iIdx: number, f: keyof ItemOpcao, v: any) => {
    const n = [...opcoesPersonalizacao];
    n[oIdx].itens[iIdx] = { ...n[oIdx].itens[iIdx], [f]: v };
    setOpcoesPersonalizacao(n);
  };

  const produtosFiltrados = produtos.filter(p => {
    if (filtroVisibilidade.delivery !== null && p.visivel_delivery !== filtroVisibilidade.delivery) return false;
    if (filtroVisibilidade.pdv !== null && p.visivel_pdv !== filtroVisibilidade.pdv) return false;
    if (filtroVisibilidade.garcom !== null && p.visivel_garcom !== filtroVisibilidade.garcom) return false;
    return true;
  });

  if (initialLoading) return <div className="flex h-64 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" /></div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-gray-100 dark:border-gray-800 pb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">Produtos</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">Gerencie seu cardápio de forma organizada.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={exportToCSV} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-5 py-3 rounded-2xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-all shadow-sm">
            <Download className="h-4 w-4" />
            Exportar p/ Corretor
          </button>

          <div className="relative">
            <input type="file" accept=".csv" onChange={handleImportCSV} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" title="Importar arquivo CSV" />
            <button type="button" className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-5 py-3 rounded-2xl font-bold hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all shadow-sm border border-blue-100 dark:border-blue-900/50">
              <FileUp className="h-4 w-4" />
              Importar em Massa
            </button>
          </div>

          <button onClick={async () => { reset(); setEditingProduto(null); setSelectedImage(null); setOpcoesPersonalizacao([]); setShowModal(true); setCodigoAutoGerado(await gerarCodigoAutomatico()); }} className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-white font-bold hover:bg-blue-700 transition-all shadow-lg">
            <Plus className="h-5 w-5" /> Adicionar Produto
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className="relative flex-1 group">
          {isSearching ? (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          )}
          <input type="text" placeholder="Pesquisar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 py-3.5 pl-12 pr-4 outline-none" />
        </div>
        <div className="flex gap-4">
          <select value={categoriaFiltro} onChange={(e) => setCategoriaFiltro(e.target.value ? parseInt(e.target.value) : '')} className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 py-3.5 px-4 outline-none">
            <option value="">Todas Categorias</option>
            {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1.5 rounded-2xl border border-gray-200 dark:border-gray-700">
             <button onClick={() => setFiltroVisibilidade(p => ({ ...p, delivery: p.delivery === null ? true : p.delivery === true ? false : null }))} className={`p-2 rounded-xl ${filtroVisibilidade.delivery === true ? 'bg-blue-600 text-white' : 'text-gray-400'}`}><Truck className="h-5 w-5" /></button>
             <button onClick={() => setFiltroVisibilidade(p => ({ ...p, pdv: p.pdv === null ? true : p.pdv === true ? false : null }))} className={`p-2 rounded-xl ${filtroVisibilidade.pdv === true ? 'bg-green-600 text-white' : 'text-gray-400'}`}><ShoppingBag className="h-5 w-5" /></button>
             <button onClick={() => setFiltroVisibilidade(p => ({ ...p, garcom: p.garcom === null ? true : p.garcom === true ? false : null }))} className={`p-2 rounded-xl ${filtroVisibilidade.garcom === true ? 'bg-orange-600 text-white' : 'text-gray-400'}`}><Users className="h-5 w-5" /></button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {produtosFiltrados.map((p) => (
          <div key={p.id} className={`group bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm transition-all overflow-hidden ${!p.ativo ? 'opacity-60 grayscale' : ''}`}>
            <div className="relative h-44 overflow-hidden bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
              {p.imagem_url ? <img src={p.imagem_url.startsWith('http') ? p.imagem_url : `${config.api.baseUrl}/${p.imagem_url}`} alt={p.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <ImageIcon className="h-10 w-10 text-gray-200" />}
              {p.destaque && <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 text-[10px] font-black px-2 py-1 rounded-full shadow-lg">DESTAQUE</div>}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3">
                <button onClick={() => handleEdit(p)} className="p-3 bg-white text-blue-600 rounded-2xl hover:scale-110 transition-transform" title="Editar"><Edit className="h-5 w-5" /></button>
                <button onClick={() => handleDelete(p.id)} className="p-3 bg-white text-red-600 rounded-2xl hover:scale-110 transition-transform" title="Excluir"><Trash2 className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="p-5 flex flex-col h-full">
              <div className="flex justify-between items-start mb-2 gap-2">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-2 flex-1">{p.nome}</h3>
                <span className="text-white text-xl font-extrabold">R$ {Number(p.preco).toFixed(2)}</span>
              </div>
              <p className="text-sm text-gray-400 line-clamp-2 mb-2 min-h-[40px]">{p.descricao || 'Sem descrição.'}</p>
              <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-4 flex flex-col gap-0.5 font-bold uppercase">
                {p.updated_at ? (
                  <span>Última alteração: {formatarData(p.updated_at)}</span>
                ) : p.created_at ? (
                  <span>Criado em: {formatarData(p.created_at)}</span>
                ) : null}
              </div>
              <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                <div className="flex gap-2">
                  {p.visivel_delivery && <Truck className="h-4 w-4 text-blue-400" />}
                  {p.visivel_pdv && <ShoppingBag className="h-4 w-4 text-green-400" />}
                  {p.visivel_garcom && <Users className="h-4 w-4 text-orange-400" />}
                </div>
                <div className="flex items-center gap-1">
                   <button onClick={() => handleDesativar(p.id, p.ativo)} className={`text-[10px] font-bold uppercase p-2 rounded-lg ${p.ativo ? 'text-red-500 hover:bg-red-50' : 'text-green-500 hover:bg-green-50'}`}>{p.ativo ? 'Inativar' : 'Ativar'}</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hasMore && produtosFiltrados.length > 0 && !loading && (
        <div className="flex justify-center mt-8 pb-8">
          <button 
            onClick={() => fetchProdutos(false)} 
            className="btn-secondary px-8 py-3 rounded-2xl font-bold border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
          >
            Carregar mais produtos
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-md">
          <div className="bg-white dark:bg-gray-900 w-full max-w-6xl h-[90vh] flex flex-col md:flex-row rounded-[2rem] shadow-2xl overflow-hidden border border-white/20">
            <div className="hidden xl:flex w-[28%] bg-gray-50/50 dark:bg-gray-800/30 p-10 flex-col items-center justify-center space-y-8 border-r border-gray-100 dark:border-gray-800">
               <h3 className="text-xs font-black text-gray-300 uppercase tracking-widest">Prévia</h3>
               <div className="w-full bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-2xl overflow-hidden">
                 <div className="h-44 bg-gray-100 dark:bg-gray-700 flex items-center justify-center relative">
                   {selectedImage ? <img src={URL.createObjectURL(selectedImage)} className="w-full h-full object-cover" /> : editingProduto?.imagem_url ? <img src={editingProduto.imagem_url.startsWith('http') ? editingProduto.imagem_url : `${config.api.baseUrl}/${editingProduto.imagem_url}`} className="w-full h-full object-cover" /> : <ImageIcon className="h-10 w-10 text-gray-200" />}
                 </div>
                 <div className="p-6">
                    <p className="font-bold text-gray-900 dark:text-white mb-1">{watch('nome') || 'Nome'}</p>
                    <p className="text-blue-600 font-bold text-sm">R$ {Number(watch('preco') || 0).toFixed(2)}</p>
                 </div>
               </div>
            </div>
            <div className="flex-1 flex flex-col">
              <div className="px-8 py-6 flex justify-between items-center border-b border-gray-100 dark:border-gray-800">
                <h2 className="text-xl font-black text-gray-900 dark:text-white">{editingProduto ? 'Editar' : 'Novo'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8">
                {editingProduto && (
                  <div className="flex flex-wrap gap-6 text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase border-b border-gray-100 dark:border-gray-800 pb-4">
                    {editingProduto.created_at && (
                      <span>Criado em: {formatarData(editingProduto.created_at)}</span>
                    )}
                    {editingProduto.updated_at && (
                      <span>Última alteração: {formatarData(editingProduto.updated_at)}</span>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-full">
                    <label className="text-xs font-bold text-gray-400 uppercase">Foto do Produto</label>
                    <div className="mt-2 flex flex-col sm:flex-row items-center gap-6 p-6 bg-gray-50 dark:bg-gray-800/40 rounded-3xl border border-dashed border-gray-200 dark:border-gray-700">
                      <div className="relative w-24 h-24 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                        {selectedImage ? (
                          <img src={URL.createObjectURL(selectedImage)} className="w-full h-full object-cover" />
                        ) : editingProduto?.imagem_url ? (
                          <img src={editingProduto.imagem_url.startsWith('http') ? editingProduto.imagem_url : `${config.api.baseUrl}/${editingProduto.imagem_url}`} className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1 space-y-2 text-center sm:text-left">
                        <p className="text-sm font-bold text-gray-800 dark:text-gray-200">
                          Selecione uma imagem para o produto
                        </p>
                        <p className="text-xs text-gray-400 font-medium">
                          Formatos aceitos: JPG, PNG ou WEBP. Recomendado tamanho quadrado.
                        </p>
                        <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 mt-2">
                          <input 
                            type="file" 
                            id="produto-imagem-input" 
                            accept="image/*" 
                            onChange={handleImageChange} 
                            className="hidden" 
                          />
                          <label 
                            htmlFor="produto-imagem-input" 
                            className="cursor-pointer inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-black px-4 py-2.5 rounded-xl shadow-md transition-all uppercase tracking-wider"
                          >
                            <Upload className="h-3.5 w-3.5" />
                            {selectedImage || editingProduto?.imagem_url ? 'Alterar Foto' : 'Enviar Foto'}
                          </label>
                          {selectedImage && (
                            <button 
                              type="button" 
                              onClick={() => setSelectedImage(null)} 
                              className="inline-flex items-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-black px-4 py-2.5 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all uppercase tracking-wider"
                            >
                              Remover Selecionada
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-full">
                    <label className="text-xs font-bold text-gray-400 uppercase">Nome *</label>
                    <input {...register('nome', { required: 'Nome é obrigatório' })} className={`w-full rounded-2xl bg-gray-50 dark:bg-gray-800 p-4 border-none outline-none font-medium ${errors.nome ? 'ring-2 ring-red-500' : ''}`} />
                    {errors.nome && <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">{errors.nome.message}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase">Código Interno / SKU (Busca Rápida)</label>
                    <input {...register('codigo')} placeholder="Gerado automaticamente" className="w-full rounded-2xl bg-gray-50 dark:bg-gray-800 p-4 border-none outline-none font-medium" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase">Código de Barras / EAN-13</label>
                    <input {...register('codigo_barras')} placeholder="Ex: 7891010101010" className="w-full rounded-2xl bg-gray-50 dark:bg-gray-800 p-4 border-none outline-none font-medium" />
                  </div>
                  <div className="col-span-full">
                    <label className="text-xs font-bold text-gray-400 uppercase">Descrição</label>
                    <textarea {...register('descricao')} rows={2} className="w-full rounded-2xl bg-gray-50 dark:bg-gray-800 p-4 border-none outline-none font-medium resize-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase">Preço *</label>
                    <input {...register('preco', { required: 'Preço é obrigatório' })} type="number" step="0.01" className={`w-full rounded-2xl bg-gray-50 dark:bg-gray-800 p-4 border-none outline-none font-bold ${errors.preco ? 'ring-2 ring-red-500' : ''}`} />
                    {errors.preco && <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">{errors.preco.message}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 uppercase">Categoria *</label>
                    <select {...register('categoria_id', { required: 'Categoria é obrigatória' })} className={`w-full rounded-2xl bg-gray-50 dark:bg-gray-800 p-4 border-none outline-none ${errors.categoria_id ? 'ring-2 ring-red-500' : ''}`}>
                      <option value="">Selecione...</option>
                      {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                    {errors.categoria_id && <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">{errors.categoria_id.message}</p>}
                  </div>
                </div>

                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-3xl space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                      <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wider">Dados Fiscais (Nuvem Fiscal)</h3>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowCopiarModal(true); setBuscaCopia(''); setProdutosCopia([]); }}
                        className="bg-white dark:bg-gray-900 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2"
                      >
                        <Search className="h-4 w-4 text-blue-500" />
                        Copiar impostos de outro produto
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase">NCM (8 dígitos) *</label>
                      <div className="relative flex items-center">
                        <input {...register('fiscal_ncm', { required: 'NCM é obrigatório', pattern: { value: /^\d{8}$/, message: 'NCM deve ter 8 dígitos' } })} placeholder="Ex: 22030000" className={`w-full rounded-xl bg-white dark:bg-gray-900 p-3 pr-12 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold ${errors.fiscal_ncm ? 'ring-2 ring-red-500' : ''}`} />
                        <button type="button" onClick={() => setShowTributosModal(true)} className="absolute right-1.5 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm" title="Pesquisar no IBPT">
                          <Search className="h-4 w-4" />
                        </button>
                      </div>
                      {errors.fiscal_ncm && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.fiscal_ncm.message}</p>}
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase">Origem ICMS *</label>
                      <select {...register('fiscal_origem')} className="w-full rounded-xl bg-white dark:bg-gray-900 p-3 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold">
                        <option value="0">0 - Nacional</option>
                        <option value="1">1 - Estrangeira (Importadora Direta)</option>
                        <option value="2">2 - Estrangeira (Adquirida no Mercado Interno)</option>
                        <option value="3">3 - Nacional (Conteúdo Importação {' > '} 40%)</option>
                        <option value="4">4 - Nacional (Produção conforme Processos Básicos)</option>
                        <option value="5">5 - Nacional (Conteúdo Importação {' <= '} 40%)</option>
                        <option value="6">6 - Estrangeira (Importação Direta - Sem Similar)</option>
                        <option value="7">7 - Estrangeira (Adquirida Mercado Interno - Sem Similar)</option>
                        <option value="8">8 - Nacional (Mercadoria ou Bem com Conteúdo Importação {' > '} 70%)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase">CFOP *</label>
                      <select {...register('fiscal_cfop', { required: 'CFOP é obrigatório' })} className={`w-full rounded-xl bg-white dark:bg-gray-900 p-3 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold ${errors.fiscal_cfop ? 'ring-2 ring-red-500' : ''}`}>
                        <option value="">Selecione CFOP...</option>
                        <option value="5101">5101 - Venda produção estab.</option>
                        <option value="5102">5102 - Venda merc. terceiros</option>
                        <option value="5403">5403 - Venda merc. ST (Substituto)</option>
                        <option value="5405">5405 - Venda merc. ST (Substituído)</option>
                      </select>
                      {errors.fiscal_cfop && <p className="text-[10px] text-red-500 font-bold mt-1">{errors.fiscal_cfop.message}</p>}
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase">CEST</label>
                      <input {...register('fiscal_cest')} placeholder="Ex: 0300100" className="w-full rounded-xl bg-white dark:bg-gray-900 p-3 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-white/50 dark:bg-gray-900/40 p-5 rounded-3xl border border-dashed border-gray-200 dark:border-gray-700">
                    <div className="space-y-4">
                      <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest border-b border-blue-100 dark:border-blue-900 pb-1">ICMS (Regime Normal ou Simples)</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                        <div>
                          <label className="text-[10px] font-black text-gray-400 uppercase">CST (Regime Normal)</label>
                          <select {...register('fiscal_cst')} className="w-full rounded-xl bg-white dark:bg-gray-900 p-3.5 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold shadow-sm">
                            <option value="00">00 - Tributada integralmente</option>
                            <option value="10">10 - Tributada com cobrança de ST</option>
                            <option value="20">20 - Com redução de base de cálculo</option>
                            <option value="40">40 - Isenta</option>
                            <option value="41">41 - Não tributada</option>
                            <option value="60">60 - ICMS cobrado anteriormente por ST</option>
                            <option value="90">90 - Outras</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-gray-400 uppercase">CSOSN (Simples Nacional)</label>
                          <select {...register('fiscal_csosn')} className="w-full rounded-xl bg-white dark:bg-gray-900 p-3.5 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold shadow-sm">
                            <option value="101">101 - Trib. com permissão de crédito</option>
                            <option value="102">102 - Tributada sem permissão de crédito</option>
                            <option value="103">103 - Isenção por faixa de receita</option>
                            <option value="201">201 - Trib. com permissão de crédito e ST</option>
                            <option value="202">202 - Trib. sem permissão de crédito e ST</option>
                            <option value="400">400 - Não tributada</option>
                            <option value="500">500 - ICMS cobrado anteriormente por ST</option>
                            <option value="900">900 - Outros</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-gray-400 uppercase">% Redução Base Cálculo</label>
                          <input type="number" step="0.01" {...register('fiscal_p_red_bc')} placeholder="0.00" className="w-full rounded-xl bg-white dark:bg-gray-900 p-3.5 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-[10px] font-black text-green-600 uppercase tracking-widest border-b border-green-100 dark:border-green-900 pb-1">PIS / COFINS</h4>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="text-[10px] font-black text-gray-400 uppercase">CST PIS/COFINS</label>
                          <select {...register('fiscal_pis_cst')} className="w-full rounded-xl bg-white dark:bg-gray-900 p-3.5 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold shadow-sm">
                            <option value="01">01 - Alíquota Básica</option>
                            <option value="04">04 - Tributação Monofásica</option>
                            <option value="06">06 - Alíquota Zero</option>
                            <option value="07">07 - Operação Isenta</option>
                            <option value="08">08 - Operação sem incidência</option>
                            <option value="09">09 - Operação com suspensão</option>
                            <option value="49">49 - Outras Operações de Saída</option>
                            <option value="99">99 - Outras Operações</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-[10px] font-black text-gray-400 uppercase">% PIS</label>
                            <input type="number" step="0.01" {...register('fiscal_pis_p')} placeholder="0.00" className="w-full rounded-xl bg-white dark:bg-gray-900 p-3.5 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold" />
                          </div>
                          <div>
                            <label className="text-[10px] font-black text-gray-400 uppercase">% COFINS</label>
                            <input type="number" step="0.01" {...register('fiscal_cofins_p')} placeholder="0.00" className="w-full rounded-xl bg-white dark:bg-gray-900 p-3.5 border border-gray-100 dark:border-gray-700 outline-none text-sm font-bold" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {(watch('fiscal_ibpt_fed') || watch('fiscal_ibpt_est')) ? (
                    <div className="mt-4 p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 flex flex-wrap gap-6 items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-blue-400 uppercase">Federal:</span>
                        <span className="text-sm font-black text-blue-700 dark:text-blue-300">{watch('fiscal_ibpt_fed')}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-blue-400 uppercase">Estadual:</span>
                        <span className="text-sm font-black text-blue-700 dark:text-blue-300">{watch('fiscal_ibpt_est')}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-blue-400 uppercase">Municipal:</span>
                        <span className="text-sm font-black text-blue-700 dark:text-blue-300">{watch('fiscal_ibpt_mun')}%</span>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="space-y-4">
                  <div className="flex flex-wrap gap-4">
                    {[
                      { id: 'visivel_delivery', label: 'Delivery' },
                      { id: 'visivel_pdv', label: 'PDV' },
                      { id: 'visivel_garcom', label: 'Garçom' },
                      { id: 'destaque', label: 'Destaque' }
                    ].map(it => (
                      <label key={it.id} className="flex items-center gap-2 p-3 rounded-xl border border-gray-100 select-none cursor-pointer">
                        <input type="checkbox" {...register(it.id as any)} className="w-4 h-4 rounded" />
                        <span className="text-xs font-bold uppercase">{it.label}</span>
                      </label>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-gray-50 dark:border-gray-800">
                    <label className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/40 cursor-pointer group">
                      <span className="text-xs font-black uppercase text-gray-500">Produto por Peso</span>
                      <input type="checkbox" {...register('venda_por_peso')} className="w-5 h-5 rounded-lg border-gray-300 text-blue-600 focus:ring-blue-500" />
                    </label>
                  </div>
                </div>
              </div>
              <div className="p-8 border-t border-gray-100 flex gap-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-4 text-gray-500 font-bold border rounded-2xl">Cancelar</button>
                <button onClick={handleSubmit(onSubmit)} className="flex-[2] py-4 bg-gray-900 text-white font-extrabold rounded-2xl shadow-xl">Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPersonalizacaoModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xl">
          <div className="bg-white dark:bg-gray-900 w-full max-w-4xl h-[85vh] flex flex-col rounded-[2.5rem] shadow-2xl overflow-hidden">
            <div className="px-10 py-8 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-black">Personalizar: {editingProduto?.nome}</h2>
              <button onClick={() => setShowPersonalizacaoModal(false)} className="p-3 hover:bg-gray-100 rounded-2xl"><X className="h-7 w-7 text-gray-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-10 space-y-12">
              {['adicionar', 'remover'].map(tipo => (
                <div key={tipo} className="space-y-6">
                  <div className="flex justify-between items-center bg-gray-50 p-6 rounded-3xl">
                    <h3 className="font-extrabold text-xl capitalize">{tipo === 'adicionar' ? 'Adicionais' : 'Remoções'}</h3>
                    <button onClick={() => addOpcao(tipo as any)} className="px-6 py-2 bg-gray-900 text-white font-black text-xs rounded-xl">+ Novo Grupo</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {opcoesPersonalizacao.filter(o => o.tipo === tipo).map((opcao) => {
                      const idx = opcoesPersonalizacao.indexOf(opcao);
                      return (
                        <div key={idx} className="p-6 border border-gray-100 rounded-[2rem] bg-white shadow-sm space-y-6">
                          <input value={opcao.nome} onChange={(e) => updateOpcao(idx, 'nome', e.target.value)} className="font-extrabold text-gray-900 text-lg bg-transparent border-b outline-none w-full" />
                          <div className="space-y-3">
                             {opcao.itens.map((item, i) => (
                               <div key={i} className="flex gap-3 items-center bg-gray-50 p-3 rounded-2xl">
                                 <input value={item.nome} onChange={(e) => updateItemOpcao(idx, i, 'nome', e.target.value)} className="flex-1 bg-transparent outline-none font-bold text-sm" />
                                 {tipo === 'adicionar' && <input type="number" value={item.preco_adicional} onChange={(e) => updateItemOpcao(idx, i, 'preco_adicional', parseFloat(e.target.value))} className="w-12 bg-transparent outline-none text-sm font-black text-blue-600" />}
                                 <button onClick={() => removeItemOpcao(idx, i)} className="text-red-400"><X className="h-4 w-4" /></button>
                               </div>
                             ))}
                          </div>
                          <button onClick={() => addItemOpcao(idx)} className="w-full py-3 border-2 border-dashed border-gray-100 rounded-2xl text-gray-400 text-[10px] font-black">+ Item</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-10 border-t flex gap-4">
              <button onClick={() => setShowPersonalizacaoModal(false)} className="flex-1 py-4 bg-white text-gray-500 font-extrabold border rounded-3xl">Descartar</button>
              <button onClick={salvarPersonalizacao} className="flex-[2] py-4 bg-blue-600 text-white font-black rounded-3xl shadow-2xl">Aplicar</button>
            </div>
          </div>
        </div>
      )}
      {showTributosModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xl">
          <div className="bg-white dark:bg-gray-900 w-full max-w-2xl h-[70vh] flex flex-col rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/10">
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-black">Pesquisar Tributos (IBPT)</h2>
                <p className="text-xs text-gray-400 font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px]">Selecione o item mais parecido com o seu produto.</p>
              </div>
              <button onClick={() => setShowTributosModal(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="h-6 w-6 text-gray-400" /></button>
            </div>
            <div className="p-6">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input 
                  autoFocus 
                  type="text" 
                  placeholder="Pesquisar por nome ou NCM..." 
                  value={tributosQuery} 
                  onChange={(e) => setTributosQuery(e.target.value)} 
                  className="w-full rounded-2xl bg-gray-50 dark:bg-gray-800 border-none py-4 pl-12 pr-4 outline-none font-bold" 
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-2">
              {searchingTributos ? (
                <div className="flex items-center justify-center h-32"><div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" /></div>
              ) : tributosResults.length > 0 ? (
                tributosResults.map((t, idx) => (
                  <div key={idx} onClick={() => selectTributo(t)} className="p-4 rounded-2xl hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 group cursor-pointer border border-transparent hover:shadow-lg transition-all">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-xs font-black uppercase text-blue-600 group-hover:text-blue-100">{t.codigo}</span>
                      <span className="text-[10px] font-bold opacity-60">Est: {t.estadual}% | Fed: {t.nacionalfederal}%</span>
                    </div>
                    <p className="text-sm font-bold leading-tight line-clamp-2">{t.descricao}</p>
                  </div>
                ))
              ) : tributosQuery.length >= 2 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                  <Search className="h-8 w-8 mb-2 opacity-20" />
                  <p className="text-sm font-medium">Nenhum resultado encontrado.</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                  <p className="text-sm font-medium">Digite pelo menos 2 letras para buscar.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCopiarModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xl">
          <div className="bg-white dark:bg-gray-900 w-full max-w-2xl h-[70vh] flex flex-col rounded-[2.5rem] shadow-2xl overflow-hidden border border-white/10">
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-black">Copiar Impostos de Outro Produto</h2>
                <p className="text-xs text-gray-400 font-medium">Busque o produto de origem para copiar NCM, ICMS, etc.</p>
              </div>
              <button onClick={() => setShowCopiarModal(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="h-6 w-6 text-gray-400" /></button>
            </div>
            <div className="p-6">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input 
                  autoFocus 
                  type="text" 
                  placeholder="Buscar produto por nome..." 
                  value={buscaCopia} 
                  onChange={(e) => setBuscaCopia(e.target.value)} 
                  className="w-full rounded-2xl bg-gray-50 dark:bg-gray-800 border-none py-4 pl-12 pr-4 outline-none font-bold" 
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-2">
              {loadingCopia ? (
                <div className="flex items-center justify-center h-32"><div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" /></div>
              ) : produtosCopia.length > 0 ? (
                produtosCopia.map((prod) => (
                  <div key={prod.id} 
                    onClick={() => {
                      setValue('fiscal_ncm', prod.fiscal_ncm || '', { shouldValidate: true });
                      setValue('fiscal_origem', prod.fiscal_origem !== null && prod.fiscal_origem !== undefined ? prod.fiscal_origem : undefined);
                      setValue('fiscal_cst', prod.fiscal_cst || '');
                      setValue('fiscal_cfop', prod.fiscal_cfop || '', { shouldValidate: true });
                      setValue('fiscal_cest', prod.fiscal_cest || '');
                      setValue('fiscal_csosn', prod.fiscal_csosn || '');
                      setValue('fiscal_p_red_bc', prod.fiscal_p_red_bc || 0);
                      setValue('fiscal_pis_cst', prod.fiscal_pis_cst || '01');
                      setValue('fiscal_cofins_cst', prod.fiscal_cofins_cst || '01');
                      setValue('fiscal_pis_p', prod.fiscal_pis_p || 0);
                      setValue('fiscal_cofins_p', prod.fiscal_cofins_p || 0);
                      setValue('fiscal_ibpt_fed', prod.fiscal_ibpt_fed || 0);
                      setValue('fiscal_ibpt_est', prod.fiscal_ibpt_est || 0);
                      setValue('fiscal_ibpt_mun', prod.fiscal_ibpt_mun || 0);
                      toast.success(`Impostos copiados de: ${prod.nome}`);
                      setShowCopiarModal(false);
                    }} 
                    className="p-4 rounded-2xl hover:bg-blue-600 hover:text-white dark:hover:bg-blue-600 group cursor-pointer border border-gray-100 dark:border-gray-800 hover:shadow-lg transition-all flex items-center justify-between mb-2"
                  >
                    <div>
                      <p className="text-sm font-bold leading-tight line-clamp-1">{prod.nome}</p>
                      <span className="text-[10px] font-black uppercase text-gray-500 group-hover:text-blue-100">NCM: {prod.fiscal_ncm || 'Não preenchido'}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                  <p className="text-sm font-medium">Nenhum produto encontrado.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProdutosPage;
